<template>
  <div>
    <h1>Transcribe Results</h1>

    <div v-if="pageLoading">
      <semipolar-spinner :animation-duration="2000" :size="48" color="#a6192e"/>
      <span>Loading and formatting the transcription.</span>
    </div>
    <div v-else>
      <b-row align-v="center">
        <b-col cols="12" lg="5">
          <span    v-show="!isUriLoading">
          <audio controls ref="player">
            <source v-bind:src="audioUri">
          </audio>
          </span>
        </b-col>
        <b-col cols="12" lg="5">
          <b-dropdown id="dropdown-left" text="Actions" variant="primary" class="m-2">
            <b-dropdown-item @click="submitCaptionRequest('srt')">Generate SRT Caption Text</b-dropdown-item>
            <b-dropdown-item @click="submitCaptionRequest('vtt')">Generate VTT Caption Text</b-dropdown-item>
          </b-dropdown>
        </b-col>
      </b-row>
      
      <b-row>
        <b-col cols="12">
          <b-tabs content-class="mt-3">
            <b-tab title="Original">
              <div class="transcriptItems" id="transcriptItems" ref="transcriptItems"> </div>
              <label for="transcribedText"><strong>Transcription Text:</strong></label>
              <b-button type="button" variant="primary" size="sm"
                v-clipboard:copy="transcribedData.results.transcripts[0].transcript"
                v-clipboard:success="onCopy">
                  <b-icon icon="clipboard"></b-icon> Copy Text to Clipboard
              </b-button>
              <b-form-textarea
                id="transcribedText"
                v-model="transcribedData.results.transcripts[0].transcript"
                rows="10"
                max-rows="30"/>
            </b-tab>

            <b-tab title="Formatted w/ Confidence">
              <b-card  header-tag="header" style="margin-bottom:18px;"
                v-for="segment in formattedSegments" v-bind:key="segment.id">
                <template v-slot:header>
                  <strong>Speaker: {{segment.speaker}}</strong>
                </template>
                <b-row>
                  <b-col cols="12">
                    <span v-for="word in segment.contents" v-bind:key="word.wordIndex" 
                      v-b-popover.hover.top="word.confidenceFormatted"
                      style="display: inline-block; margin-right: .25em;">
                      {{word.word}}
                    </span>
                  </b-col>
                </b-row>
              </b-card>
            </b-tab>

            <b-tab title="Captions">
              <div v-if="captionText.body">
                <label for="captionedText"><strong>Caption Text:</strong></label>
                <b-button type="button" variant="primary" size="sm"
                  v-clipboard:copy="captionText.body"
                  v-clipboard:success="onCopy">
                    <b-icon icon="clipboard"></b-icon> Copy Text to Clipboard
                </b-button>
                <b-form-textarea
                  id="captionedText"
                  v-model="captionText.body"
                  rows="10"
                  max-rows="30"/>
              </div>
              <div v-else>
                <p class="m5">Use the action button above to select which type of caption text you would like generated.</p>
              </div>
            </b-tab>        
          </b-tabs>
        </b-col>
      </b-row>
  </div>
  </div>
</template>

<script>
  import config from '@/config'
  import axios from 'axios';
  import axiosRetry from 'axios-retry';
  import { validationMixin } from 'vuelidate'
  import { required, minLength } from 'vuelidate/lib/validators'
  import { SemipolarSpinner  } from 'epic-spinners'

  axiosRetry(axios, { retries: 3,
                      retryDelay: axiosRetry.exponentialDelay  });

export default {
mixins: [validationMixin],

props: [
    'id'
],

components: {
  SemipolarSpinner
},

data: () => ({
  transcribedData: {}, 
  transcribedItems: {},
  transcribedSpeakers: {},
  captionText: {},
  audioUri: null,    
  isLoading: false,
  isUriLoading: true,
  lastWordIndex: 0,
  nextSpeakerItem: 0,
  nextSegment: 0,
  formattedSegments: [],
  pageLoading: true
}),

mounted () {
  
},

created () { this.initialize()},

methods: {
  fetchData() {
    this.isLoading = true

    let payload = {
      jobName: this.id
    }

    axios.all([axios.post(config.CLOUDDRIVE_API_ENDPOINT + 'transcribe/results/json/transcripts', payload),
               axios.post(config.CLOUDDRIVE_API_ENDPOINT + 'transcribe/results/json/items', payload),
               axios.post(config.CLOUDDRIVE_API_ENDPOINT + 'transcribe/results/json/speakerlabels', payload)])
    .then(axios.spread((dataResponse, itemResponse, speakerResponse) => {  

        this.transcribedData = dataResponse.data
        this.transcribedItems = itemResponse.data
        this.transcribedSpeakers = speakerResponse.data

        this.formatTranscription()
        this.pageLoading = false
     }))
     .catch(error => console.log(error));
  },

  submitCaptionRequest(outputType) {
    let payload = {
      s3_bucket: this.transcribedData.bucketName,
      s3_key: this.transcribedData.objectName,
      output_type: outputType
    }
    
    axios.post(config.CLOUDDRIVE_API_ENDPOINT + 'transcribe/caption', payload)
      .then(response => {
        this.captionText = response.data
        this.captionTextType = outputType
        this.isLoading = false
      })
  },

  fetchMediaUri() {
    this.isUriLoading = true;
    let payload = {
      jobName: this.id
    }

    axios.post(config.CLOUDDRIVE_API_ENDPOINT + 'transcribe/results/audio', payload)
      .then(response => {
          this.audioUri = response.data;
          this.isUriLoading = false;
      })
  },

  initialize() {
    this.pageLoading = true
    this.fetchData()
    this.fetchMediaUri()
  },

  onCopy: function (e) {
    alert('Text copied to clipboard.')
  },
      
  formatTranscription() {
    if (!this.transcribedData.results) {
        return null
    }

    const words = this.transcribedItems.results.items
    let wordIndex = 0;
    const speakerSegments = this.transcribedSpeakers.results.speaker_labels.segments

    const x = speakerSegments.map((segment, segmentIndex) => { 
        let sentenceEndTime

        if (segmentIndex < speakerSegments.length - 1) {
            sentenceEndTime = Number(speakerSegments[segmentIndex + 1].start_time)
        } else {
            sentenceEndTime = Number(segment.end_time)
        }

        const contents = []
        let shouldStop = false
    
        while (!shouldStop) {
            const wordItem = words[wordIndex]
            if (wordItem && (!wordItem.end_time || Number(wordItem.end_time) <= sentenceEndTime)) {
                wordIndex++
                const confidence = wordItem.alternatives[0].confidence
                const isPunctuation = confidence === null

                // put a space before every word except punctuation
                const word = `${isPunctuation ? '' : ' '}${wordItem.alternatives[0].content}`
                if (isPunctuation) {
                    contents.push({
                      word,
                      wordIndex
                    })
                } else {
                    contents.push(
                      {
                        word,
                        wordIndex,
                        confidence,
                        confidenceFormatted: parseFloat(confidence * 100.0).toFixed(1) + "%"
                      }                       
                    )
                }
            } else {
                shouldStop = true
            }
        }
        return {
          id: segmentIndex,
          speaker: segment.speaker_label,
          contents: contents
        }
      })
      this.formattedSegments = x
    }
  }
}
</script>